// oracleClassLabels.ts — canonical mapping from the `oracle_class`
// integer (1..4) on `public.oracle_sources` to the plain-English label
// shown on market cards and the focus-modal RESOLUTION row. Per I-068
// Brief 2 design call: the schema column stays INTEGER for clean joins
// and ordering; the label mapping is UI-only and lives here as the
// single source of truth so future amendments touch one file.
//
// Class 4 is the documented floor (aggregators) and never used on
// Arena; the entry is kept so the mapping covers the full enum and
// so any UI accidentally reading a class-4 row gets graceful copy
// rather than an empty string.
//
// Settlement-window phrasing is also class-aware: canonical sources
// (class 1) and official data (class 2) talk about "publication";
// news sources (class 3) talk about "reporting plus correction
// window". Used by MarketFocusModal's RESOLUTION row explainer copy.

export const ORACLE_CLASS_LABELS: Record<number, string> = {
  1: 'canonical source',
  2: 'official data',
  3: 'news source',
  4: 'aggregator',
};

export function oracleClassLabel(oracleClass: number | null | undefined): string {
  if (oracleClass == null) return '';
  return ORACLE_CLASS_LABELS[oracleClass] ?? '';
}

// Tooltip copy surfaced on hover / tap of the inline class label in
// the focus-modal RESOLUTION row. Implementation in v1 is the
// browser-native `title` attribute (per brief §4.3 — polish to a
// styled tooltip component later if useful).
const ORACLE_CLASS_TOOLTIPS: Record<number, string> = {
  1: 'This market resolves against the entity responsible for publishing the fact. Settlement is typically fast and authoritative.',
  2: 'This market resolves against an official data feed — government statistics, central bank data, or regulator filings. One step removed from the originating institution but still authoritative.',
  3: 'This market resolves against a reputable news organisation. Fast and reliable for reporting results, but not the originating record. Disputes escalate to manual review.',
  4: 'Aggregator sources are not used on the Agoracle Arena.',
};

export function oracleClassTooltip(oracleClass: number | null | undefined): string {
  if (oracleClass == null) return '';
  return ORACLE_CLASS_TOOLTIPS[oracleClass] ?? '';
}

// Settlement-window phrasing for the focus-modal RESOLUTION row's
// second line. Class-aware framing per brief §4.4: canonical sources
// reference publication; news sources reference reporting plus a
// correction window. The phrasing covers the short-holdback case
// (e.g. 1h for BoE) and the standard 24/48h cases.
export function settlementWindowCopy(
  oracleClass:  number | null | undefined,
  holdbackHours: number | null | undefined,
): string {
  if (holdbackHours == null) return '';
  const hourWord = holdbackHours === 1 ? 'hour' : 'hours';
  const window   = `${holdbackHours} ${hourWord}`;

  switch (oracleClass) {
    case 1:
    case 2:
      return holdbackHours <= 2
        ? `Settlement window: ${window} after publication. The source publishes at a known time; the short window catches any immediate amendment.`
        : `Settlement window: ${window} after publication. This catches any disciplinary review or amendment by the publishing authority.`;
    case 3:
      return `Settlement window: ${window} after the result is reported. The window allows time for any correction or update by the news source.`;
    default:
      return `Settlement window: ${window}.`;
  }
}
